import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import { NovaLogo } from './NovaLogo';

// Editorial Footer — light warm-cream backdrop that visually separates from
// the black final-CTA section above and continues the pastel theme.
const Footer = () => {
  const sections = [
    {
      title: 'Product',
      items: [
        { to: '/', label: 'Home' },
        { to: '/features', label: 'Features' },
        { to: '/security', label: 'Security' },
        { to: '/pricing', label: 'Pricing' },
        { to: '/trust', label: 'Trust Center' },
      ],
    },
    {
      title: 'Company',
      items: [
        { to: '/about', label: 'About Us' },
        { to: '/contact', label: 'Contact' },
      ],
    },
    {
      title: 'Trust & Legal',
      items: [
        { to: '/privacy-policy', label: 'Privacy Policy' },
        { to: '/terms-of-service', label: 'Terms of Service' },
        { to: '/dpa', label: 'DPA' },
      ],
    },
    {
      title: 'Get Started',
      items: [
        { to: '/login', label: 'Sign In' },
        { to: '/register', label: 'Get Started' },
      ],
    },
  ];

  return (
    <footer className="bg-[#F4F1EC] dark:bg-[#141414] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      {/* Three-stripe pastel hairline at the very top — visual handshake with Home */}
      <div className="border-t border-black/10 dark:border-white/10" />

      <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 lg:gap-12">
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <NovaLogo className="mb-4" markClassName="h-8 w-8" textClassName="text-2xl" />
            <p className="text-black/65 dark:text-white/65 text-base leading-relaxed max-w-xs">
              AI-Powered Compliance and Certification Platform for SOC 2, ISO 27001, PCI DSS, GDPR and HIPAA.
            </p>
          </div>

          {sections.map((s) => (
            <div key={s.title}>
              <h4 className="font-label font-semibold uppercase tracking-[0.18em] text-xs text-black/65 dark:text-white/65 mb-5">
                {s.title}
              </h4>
              <ul className="space-y-3">
                {s.items.map((it) => (
                  <li key={it.to}>
                    <Link
                      to={it.to}
                      className="text-[15px] text-black/75 dark:text-white/75 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] hover:underline underline-offset-4 decoration-[#EBD8C8] decoration-2 transition-colors"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-black/10 dark:border-white/10 mt-14 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-label uppercase tracking-[0.22em] text-[10px] text-black/45 dark:text-white/50">
            © {new Date().getFullYear()} Nova by{' '}
            <a
              href="https://www.theinceptica.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] hover:underline underline-offset-4 decoration-[#EBD8C8] decoration-2 transition-colors"
              data-testid="footer-inceptica-link"
            >
              Inceptica
            </a>
            . All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a
              href="https://www.linkedin.com/company/inceptica"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Inceptica on LinkedIn"
              className="text-black/55 dark:text-white/60 hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-colors"
              data-testid="footer-linkedin-link"
            >
              <Linkedin className="h-4 w-4" strokeWidth={1.75} />
            </a>
            <p className="font-label uppercase tracking-[0.22em] text-[10px] text-black/45 dark:text-white/50">
              Compliance, quietly engineered
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
